import "@/styles/globals.css"
import type { AppProps } from "next/app"
import theme from "../styles/theme"
import GlobalHeaderComponent from "../components/GlobalHeaderComponent"
import MobileGlobalHeaderComponent from "../components/MobileGlobalHeaderComponent"
import * as amplitude from "@amplitude/analytics-browser"
import Script from "next/script"
import FacebookPixel from "../components/FacebookPixel"
import Sidebar from "@/components/Sidebar"
import useSidebarStore from "@/store/SidebarStore"
import useLayoutConfigStore from "@/store/LayoutConfigStore"
import {
  KEYWORD_DASHBOARD_PAGE_BG,
  KEYWORD_DASHBOARD_ROUTES,
} from "@/constants/KeywordDashboardTheme"
import { ChakraProvider, Box, Flex } from "@chakra-ui/react"
import { useMediaQuery } from "@chakra-ui/react"
import { NextIntlClientProvider } from "next-intl"
import { useRouter } from "next/router"
import { useEffect } from "react"

export default function App({ Component, pageProps }: AppProps) {
  const [isDesktop] = useMediaQuery("(min-width: 800px)")
  const [isMobile] = useMediaQuery("(max-width: 480px)")
  const GAID = process.env.NEXT_PUBLIC_GA_ID
  const router = useRouter()
  const { pathname } = router
  const {
    config: layoutConfig,
    resetConfig,
    setConfig,
  } = useLayoutConfigStore()
  const { isOpen: isSidebarOpen } = useSidebarStore()

  // Reset layout config on route change
  useEffect(() => {
    resetConfig()
  }, [pathname, resetConfig])

  // Set full width for landing pages
  useEffect(() => {
    const isLandingPage =
      pathname === "/features/keyword" ||
      pathname === "/" ||
      pathname === "/signup"
    if (isLandingPage) {
      setConfig({ fullWidth: true })
    }
  }, [pathname, setConfig])

  // Set left-aligned layout for specific pages (content follows sidebar, not centered)
  const routesLeftAligned = ["/threads/keyword"]
  useEffect(() => {
    const isLeftAlignedPage = routesLeftAligned.some((route) =>
      pathname.startsWith(route),
    )
    if (isLeftAlignedPage) {
      setConfig({ leftAligned: true })
    }
  }, [pathname, setConfig])

  const routesWithSidebar = [
    "/dashboard",
    "/threads/user/posts",
    "/threads/reply-manager",
    "/threads/keyword",
    "/threads/schedule-post",
    "/threads/multi-post",
    "/threads/mentions-manager",
    "/threads/competitor-analysis",
    "/threads/connect",
    "/team",
  ]

  const shouldShowSidebar =
    (routesWithSidebar.some((route) => pathname.startsWith(route)) &&
      !isMobile) ||
    (isMobile && isSidebarOpen)

  const header = isMobile ? (
    <MobileGlobalHeaderComponent locale={router.locale} />
  ) : (
    <GlobalHeaderComponent locale={router.locale} />
  )

  amplitude.init(process.env.NEXT_PUBLIC_AMPLITUDE_ID || "", undefined, {
    minIdLength: 1,
  })

  // Calculate final width constraints
  const maxWidth =
    layoutConfig.fullWidth || layoutConfig.leftAligned || isMobile
      ? "100%"
      : layoutConfig.maxWidth || "1100px"
  const minWidth = isMobile
    ? "100%"
    : layoutConfig.minWidth || (isDesktop ? "600px" : "100%")

  // Pages that use a darker page surface instead of the default white
  const pageBg = KEYWORD_DASHBOARD_ROUTES.some((route) =>
    pathname.startsWith(route),
  )
    ? KEYWORD_DASHBOARD_PAGE_BG
    : "ui.foreground1"

  return (
    <NextIntlClientProvider
      locale={router.locale}
      timeZone="Asia/Taipei"
      messages={pageProps.messages}>
      <ChakraProvider theme={theme}>
        <FacebookPixel />
        <Script
          src={`https://www.googletagmanager.com/gtag/js?id=G-${GAID}`}
          strategy="afterInteractive"
        />
        <Script id="google-analytics" strategy="afterInteractive">
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-${GAID}');
          `}
        </Script>

        <Box height="100vh" overflow="hidden">
          {/* Header */}
          <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            zIndex={20}
            bg="white">
            {header}
          </Box>

          <Box
            position="fixed"
            top="68px"
            left={0}
            right={0}
            bottom={0}
            overflow="hidden">
            <Flex height="100%">
              {shouldShowSidebar && (
                <Box
                  width={isMobile ? "100%" : "250px"}
                  height="100%"
                  overflowY="auto"
                  bg="white"
                  borderRight="1px solid"
                  borderColor="gray.200"
                  position={isMobile ? "fixed" : "relative"}
                  top={isMobile ? "68px" : "0"}
                  left="0"
                  zIndex={9999}>
                  <Sidebar />
                </Box>
              )}
              <Box
                flex={1}
                overflowY="auto"
                marginLeft={
                  isMobile
                    ? "0"
                    : shouldShowSidebar && !layoutConfig.leftAligned
                      ? "250px"
                      : "0"
                }
                transition="margin 0.3s ease-in-out"
                bg={pageBg}>
                <Box
                  flex={1}
                  overflowY="auto"
                  bg={pageBg}
                  maxWidth={maxWidth}
                  minWidth={minWidth}
                  marginLeft={layoutConfig.leftAligned ? "64px" : undefined}
                  marginRight={layoutConfig.leftAligned ? "64px" : undefined}
                  marginX={layoutConfig.leftAligned ? undefined : "auto"}
                  paddingLeft={layoutConfig.leftAligned ? "24px" : undefined}>
                  <Component {...pageProps} />
                </Box>
              </Box>
            </Flex>
          </Box>
        </Box>
      </ChakraProvider>
    </NextIntlClientProvider>
  )
}
